import {offlineDb} from './db';
import {storeSessionFile} from './files';

/** Runs `worker` over `items` with at most `concurrency` in flight. */
export const runPool = async <T>(
  items: T[],
  concurrency: number,
  worker: (item: T) => Promise<void>
): Promise<void> => {
  let index = 0;
  const runners = Array.from(
    {length: Math.min(concurrency, items.length)},
    async () => {
      while (index < items.length) {
        await worker(items[index++]);
      }
    }
  );
  await Promise.all(runners);
};

/** refKeys already downloaded into the session — the resume set for a refresh. */
export const getSessionCachedRefKeys = async (
  sessionId: string
): Promise<Set<string>> => {
  const files = await offlineDb.files.where('sessionId').equals(sessionId).toArray();
  return new Set(
    files.filter(file => file.kind === 'cached').map(file => file.refKey)
  );
};

export interface CachedFileTarget {
  /** Stable key the reading UI looks the blob back up by. */
  refKey: string;
  name: string;
  /** Fully-resolved (authed) URL to download from. */
  url: string;
}

export interface CacheFilesArgs {
  sessionId: string;
  targets: CachedFileTarget[];
  concurrency?: number;
  retries?: number;
  /** Called after every target settles, with the running totals. */
  onProgress?: (done: number, failed: number) => void;
}

export interface CacheFilesResult {
  done: number;
  failed: number;
}

const DEFAULT_CACHE_CONCURRENCY = 4;
const DEFAULT_CACHE_RETRIES = 2;

const fetchBlobWithRetry = async (
  url: string,
  retries: number
): Promise<Blob> => {
  for (let attempt = 0; ; attempt++) {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`File download failed (${response.status}).`);
      }
      return await response.blob();
    } catch (error) {
      if (attempt >= retries) throw error;
    }
  }
};

// Downloads each target into the session's files table as a `cached` blob. A
// missing or unreachable file is counted, never thrown — it must not block sync.
export const cacheFilesForSession = async ({
  sessionId,
  targets,
  concurrency = DEFAULT_CACHE_CONCURRENCY,
  retries = DEFAULT_CACHE_RETRIES,
  onProgress,
}: CacheFilesArgs): Promise<CacheFilesResult> => {
  let done = 0;
  let failed = 0;

  await runPool(targets, concurrency, async target => {
    try {
      const blob = await fetchBlobWithRetry(target.url, retries);
      await storeSessionFile({
        sessionId,
        kind: 'cached',
        refKey: target.refKey,
        name: target.name,
        contentType: blob.type,
        blob,
      });
      done += 1;
    } catch {
      failed += 1;
    }
    onProgress?.(done, failed);
  });

  return {done, failed};
};
